/**
 * useDirectChatNotifier — polling hook that plays a sound when the
 * logged-in viewer receives a new direct-chat message.
 *
 * Powered by /api/direct-chat/cue (10 s cadence) + /api/direct-chat/unread
 * for the badge count. Deliberately polling and NOT websockets — the
 * app has no realtime infra yet and cue/unread are cheap Mongo reads.
 *
 * Sound cue: an inline base64 blip so we don't ship an extra static
 * asset. Falls back gracefully if audio is blocked (autoplay policy).
 */
import * as React from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Short chime — 22 ms 880Hz sine sweep, ~2.5 kB base64
const CHIME_URI = "data:audio/wav;base64,UklGRlADAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YSwDAAA6/wUAvvpVAtP2CQ2S9vwWGvpEHqYAxSGqCUcgKxT8GSAe3xJnJTsLpylTA1krzvvXKfL0aSVK7wggmusgGTvpAxLl6BILRes2Bo3xIQVE+jsIFwUUEIcQtxo3G18kMSJcKPomrShrJ9YkgSY3G/QhwQ2NG//zVBHU2/oAOMawA/K1yQzArz4YFrY7JeXFuykq0z9j40sgQvXvGgIILhCUFC0AjBaZ7VkTRuBpDpjZlgza2sULj+CBBt3q2P1L9Lb1TP7z8Cn/g/EQ/vX0jP0J+Ir9J/tt/O/8UvvA/qz4hADD97kAoPmm/wf9G/6XAA79lgIF/BwCiPsFAaz70v9M+9/+iPq6/aH54PxK+VP8pfnH+/j61PsF/ff7/f4S/N//aP1WAKb+lAA/AJ/+cQEV/AACOfrAAWX58AB6+fH+xfnv/O/6yfte/QP7fgAI+2QDW/vgAxr7HAQP+n0EhPmYBOP44QOL+CkDGvhwAgH4WgH9+B0AS/nk/j36Cf+M+8oAd/2NAicAdgIiA0YCLwXtAcYFcgHgBIQBqQIsA4AATATV/8UDy//lApv+bwFm/EYAsvot/6z6ff5X+7z9c/uy/UD79/1L+xQOhPuJDrn79w4V/PYObPy+DZv8pQpi/DYFuvv6Ay77Vv+A+iL8FvpS+ub5Yfmm+dr35vlC96D5J/mB+ZH7v/lp/Br6ov5S+2sAF/34AJn9GAJi/tsBLQC0AJ0Bt/8DAtb/fgH0/74C6/8LBGkAWQaqAF8IdwAyCiIAaAt+/2sMkP7VDIL9iAxN/HcMLPvSC9v6MgtV+ncKw/nJCS35eQno+M0Iw/glCJj4kAdV+G8H0veaB0T3LAgG9wcJRfaHCV72jgok9/QKmvfyC7L4bwsX+u4KJ/urCV38ngj7/OcHFP2NB43+dwc//zAHo//dBogAKgYtASoGRAKfBesCXAaKA5cGWQNOB2wCigfSAd8HYAHYB4kAvQckAJoHKgB1B/EAcAdiAXsHAgLxB2ACdgcqAgYHmAG3BhwBrAaCARIH/wGrB0MCTQg2AtcICgIvCbcBOAkiATIJGwEXCTQBGwlXAWMJXwGZCVUBmglqAXAJcgHnCB4B";

interface UnreadInfo {
  total: number;
  perThread: Record<string, number>;
}

let cachedTimestamp: string | null = null;
let cachedUserId: string | null = null;

export function useDirectChatNotifier() {
  const { user } = useAuth();
  const [unread, setUnread] = React.useState<UnreadInfo>({ total: 0, perThread: {} });
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const seenIds = React.useRef<Set<string>>(new Set());

  // Reset the module-level cache whenever the auth identity flips so a
  // fresh login can't inherit the previous user's `since` cursor.
  React.useEffect(() => {
    const uid = user?.user_id || null;
    if (uid !== cachedUserId) {
      cachedTimestamp = null;
      cachedUserId = uid;
      seenIds.current = new Set();
    }
  }, [user]);

  // Lazy audio element (cannot instantiate at import time in SSR).
  React.useEffect(() => {
    if (typeof Audio === "undefined") return;
    const a = new Audio(CHIME_URI);
    a.volume = 0.35;
    audioRef.current = a;
    return () => { audioRef.current = null; };
  }, []);

  const refreshUnread = React.useCallback(async () => {
    if (!user) {
      setUnread({ total: 0, perThread: {} });
      return;
    }
    try {
      const r = await api.get("/direct-chat/unread");
      const perThread: Record<string, number> = {};
      for (const t of (r.data?.threads || [])) {
        perThread[t.thread_id] = t.unread || 0;
      }
      setUnread({ total: r.data?.total_unread || 0, perThread });
    } catch {
      /* best-effort */
    }
  }, [user]);

  React.useEffect(() => {
    if (!user) return;
    let alive = true;
    let timer: number | null = null;

    const poll = async () => {
      // Pause polling when the tab is hidden to save request budget +
      // avoid piling up on unread cues the user isn't seeing anyway.
      if (typeof document !== "undefined" && document.hidden) {
        if (alive) timer = window.setTimeout(poll, 15_000);
        return;
      }
      try {
        const r = await api.get("/direct-chat/cue", {
          params: cachedTimestamp ? { since: cachedTimestamp } : {},
        });
        if (!alive) return;
        cachedTimestamp = r.data?.checked_at || cachedTimestamp;
        const evs = r.data?.events || [];
        let bumped = false;
        for (const e of evs) {
          if (seenIds.current.has(e.message_id)) continue;
          seenIds.current.add(e.message_id);
          bumped = true;
        }
        if (bumped) {
          try { await audioRef.current?.play(); } catch { /* autoplay blocked */ }
          refreshUnread();
        }
      } catch {
        /* swallow */
      } finally {
        if (alive) timer = window.setTimeout(poll, 10_000);
      }
    };

    refreshUnread();
    timer = window.setTimeout(poll, 4_000);

    return () => {
      alive = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [user, refreshUnread]);

  return { unread, refreshUnread };
}
